import React, { useEffect, useState } from "react";

import { reviewsApi } from "api";
import Button from "components/ui/Button";

const LABELS = ["Poor", "Fair", "Good", "Very good", "Excellent"];

function Star({ filled }) {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
      <path
        d="M12 2.8l2.8 5.8 6.3.9-4.6 4.4 1.1 6.3L12 17.2l-5.6 3 1.1-6.3L2.9 9.5l6.3-.9z"
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// "Rate this class" in the player's course card: 1 to 5 stars and an
// optional note. A student rates a class once and can change it later; it
// shows on the class page's stars and "What learners say".
export default function RateClass({ courseId }) {
  const [review, setReview] = useState(undefined); // undefined: loading, null: none yet
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState(null); // null | "saving" | "saved" | error text

  useEffect(() => {
    reviewsApi
      .mine(courseId)
      .then((r) => setReview(r ?? null))
      .catch(() => setReview(null));
  }, [courseId]);

  function start() {
    setRating(review?.rating ?? 0);
    setNote(review?.note ?? "");
    setStatus(null);
    setOpen(true);
  }

  async function save(e) {
    e.preventDefault();
    if (!rating) return setStatus("Pick 1 to 5 stars.");
    setStatus("saving");
    try {
      const saved = review ? await reviewsApi.update(review.id, rating, note.trim()) : await reviewsApi.create(courseId, rating, note.trim());
      setReview(saved);
      setOpen(false);
      setStatus("saved");
    } catch (error) {
      const message = error?.response?.data?.message;
      setStatus(typeof message === "string" ? message : "Could not save your rating. Please try again.");
    }
  }

  if (review === undefined) return null;
  const shown = hover || rating;

  if (!open)
    return (
      <div className="yc-rate">
        {review ? (
          <p className="yc-rate-summary">
            <span className="yc-rate-stars is-small" aria-label={`Your rating: ${review.rating} of 5`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} filled={n <= review.rating} />
              ))}
            </span>
            {status === "saved" ? "Thanks for rating!" : "Your rating"}
          </p>
        ) : null}
        <Button variant="secondary" size="sm" onClick={start}>
          {review ? "Edit rating" : "Rate this class"}
        </Button>
      </div>
    );

  return (
    <form className="yc-rate is-open" onSubmit={save}>
      <p className="yc-rate-title">How would you rate this class?</p>
      <div className="yc-rate-stars" role="radiogroup" aria-label="Rating" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={rating === n}
            aria-label={`${n} star${n === 1 ? "" : "s"}, ${LABELS[n - 1]}`}
            className={n <= shown ? "is-on" : ""}
            onMouseEnter={() => setHover(n)}
            onClick={() => {
              setRating(n);
              setStatus(null);
            }}
          >
            <Star filled={n <= shown} />
          </button>
        ))}
        <span className="yc-rate-label">{shown ? LABELS[shown - 1] : ""}</span>
      </div>
      <label className="sr-only" htmlFor="yc-rate-note">
        Your review (optional)
      </label>
      <textarea
        id="yc-rate-note"
        className="yc-input yc-rate-note"
        rows={3}
        maxLength={1000}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What did you like, and what could be better? (optional)"
      />
      {status && status !== "saving" && status !== "saved" && (
        <p className="yc-error" role="alert">
          {status}
        </p>
      )}
      <div className="yc-rate-actions">
        <Button type="submit" size="sm" disabled={status === "saving"}>
          {status === "saving" ? "Saving…" : review ? "Save rating" : "Submit rating"}
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
